import { Dialog, DialogActions, DialogContent, DialogTitle, Grid } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardActionArea from "@material-ui/core/CardActionArea";
import CardActions from "@material-ui/core/CardActions";
import CardContent from "@material-ui/core/CardContent";
import CssBaseline from "@material-ui/core/CssBaseline";
import Link from "@material-ui/core/Link";
import Typography from "@material-ui/core/Typography";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import Cancel from "@material-ui/icons/Cancel";
import CheckCircle from "@material-ui/icons/CheckCircle";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DescriptionIcon from "@material-ui/icons/Description";
import SearchIcon from "@material-ui/icons/Search";
import LabelsContext from "../../../../lang/LabelsContext";
import { dateViewDMY, getReviewStatus } from "../../../../utils/Utils";
import EnumGroup from "../../../../utils/enum/EnumGroup";
import EnumReviewStatus from "../../../../utils/enum/EnumReviewStatus";
import CopyText from "../../../system/copy-text/CopyText";
import OriginalFiles from "../../../system/original-files/OriginalFiles";
import ReviewDialog from "../../../system/review-dialog/ReviewDialog";
import ArticleViewService, { ArticleViewServiceType } from "./ArticleViewService";
import useStyles from "./ArticleViewStyle";

interface DialogType {
  icon: JSX.Element;
  iconText: string;
  desc: string;
  status: string;
}

const REVIEWER_SELECTION_PROGRESS = [EnumReviewStatus.REVIEWER_SELECTION, EnumReviewStatus.ASSOCIATE_EDITOR];
const UNDER_REVIEW_PROGRESS = [EnumReviewStatus.UNDER_REVIEW, EnumReviewStatus.RETURNED_TO_EDITOR];
const REVIEWED_PROGRESS = [
  EnumReviewStatus.REJECTED,
  EnumReviewStatus.ACCEPTED,
  EnumReviewStatus.MAJOR_REVISION,
  EnumReviewStatus.MINOR_REVISION,
  EnumReviewStatus.REVIEWED,
  EnumReviewStatus.DIRECT_REJECT,
  EnumReviewStatus.REJECTED_R0,
  EnumReviewStatus.DIRECT_REJECT_R0,
  EnumReviewStatus.REJECT_AND_RESUBMIT_AS_A_LETTER_TO_THE_EDITOR,
];

const REVIEW_NOT_STATUS = [
  EnumReviewStatus.DRAFT,
  EnumReviewStatus.NEW_ARTICLE,
  EnumReviewStatus.REVIEWED,
  EnumReviewStatus.UNDER_REVIEW,
  EnumReviewStatus.REVIEWER_SELECTION,
  EnumReviewStatus.ASSOCIATE_EDITOR,
  EnumReviewStatus.RETURNED_TO_EDITOR,
  EnumReviewStatus.CORRECTION,
];

export default function ArticleView() {
  const classes = useStyles();

  const {
    articleList,
    selectedArtcle,
    setSelectedArticle,
    openDialog,
    handleOpenDialog,
    handleCloseDialog,
    articleDialog,
    reviewsArticle,
    setReviewsArticle,
    authData,
  }: ArticleViewServiceType = ArticleViewService();

  const { Labels } = LabelsContext();

  const showArticleAuthors = (authors: any) => {
    return authors.map((x: any) => `${x.firstName} ${x.lastName}`).join(", ");
  };

  const dialogItemArray = [
    {
      icon: <CloudUploadIcon fontSize="large" />,
      iconText: "New Article",
      desc: "Editorial secretary has approved your article",
      status: EnumReviewStatus.NEW_ARTICLE,
    },
    {
      icon: <DescriptionIcon fontSize="large" />,
      iconText: "Reviewer selection",
      desc: "The requested reviewers have accepted to review your",
      status: EnumReviewStatus.REVIEWER_SELECTION,
    },
    {
      icon: <SearchIcon fontSize="large" />,
      iconText: "Under Review",
      desc: "Our reviewers have completed reviewing your article",
      status: EnumReviewStatus.UNDER_REVIEW,
    },
    {
      icon: <AssignmentTurnedInIcon fontSize="large" />,
      iconText: "Reviewed",
      desc: "The editor in chief has made a decision concerning your",
      status: EnumReviewStatus.REVIEWED,
    },
  ];

  const checkProgress = (statusArticle: string, status: string) => {
    switch (status) {
      case EnumReviewStatus.NEW_ARTICLE:
        return statusArticle !== EnumReviewStatus.DRAFT;
      case EnumReviewStatus.REVIEWER_SELECTION:
        return [...REVIEWER_SELECTION_PROGRESS, ...REVIEWED_PROGRESS, ...UNDER_REVIEW_PROGRESS].includes(statusArticle);
      case EnumReviewStatus.UNDER_REVIEW:
        return [...REVIEWED_PROGRESS, ...UNDER_REVIEW_PROGRESS].includes(statusArticle);
      case EnumReviewStatus.REVIEWED:
        return REVIEWED_PROGRESS.includes(statusArticle);
      default:
        return false;
    }
  };

  const getStatus = (status: string) => {
    switch (status) {
      case EnumReviewStatus.ASSOCIATE_EDITOR:
        return "Reviewer selection";
      case EnumReviewStatus.RETURNED_TO_EDITOR:
        return "Under review";
      default:
        return getReviewStatus(status, Labels);
    }
  };

  return (
    <>
      <CssBaseline />
      {articleList?.length === 0 && (
        <Grid>
          <Typography gutterBottom variant="h5" component="h2">
            No submited article
          </Typography>
        </Grid>
      )}
      <div className={classes.container}>
        {articleList?.map((article: any) => (
          <Card key={article.id} className={classes.root}>
            <CardActionArea>
              <CardContent>
                <Typography gutterBottom variant="h6" component="h2">
                  #{article.articleId} - {article.articleTitle}
                  <CopyText text={`#${article.articleId} ${article.articleTitle}`} />
                </Typography>
                <Typography>{article.articleAuthors ? showArticleAuthors(article.articleAuthors) : ""}</Typography>
                <Typography>{getStatus(article.status)} - No action required</Typography>
                <Typography>
                  {Labels.SUBMITTED_ON} {dateViewDMY(article.uploadDate)}
                  <br />
                  {Labels.LAST_UPDATE} {dateViewDMY(article.lastUpdate)}
                </Typography>
              </CardContent>
            </CardActionArea>
            <CardActions>
              {article.files.length > 0 && (
                <Button size="small" variant="contained" onClick={() => setSelectedArticle(article)}>
                  {Labels.ORIGINAL_FILES}
                </Button>
              )}
              {article.files.find((f: any) => f.type === "CoverLetter") && (
                <Link
                  href={article.files.find((f: any) => f.type === "CoverLetter")?.location}
                  color="inherit"
                  component="a"
                  target="_blank"
                >
                  <Button size="small" variant="contained">
                    Cover letter
                  </Button>
                </Link>
              )}
              {article.files.find((f: any) => f.type === "Abstract") && (
                <Link
                  href={article.files.find((f: any) => f.type === "Abstract")?.location}
                  color="inherit"
                  component="a"
                  target="_blank"
                >
                  <Button size="small" variant="contained">
                    Abstract
                  </Button>
                </Link>
              )}
              {![EnumGroup.GUEST, EnumGroup.AUTHOR, EnumGroup.REVIEWER].includes(authData?.user.group.group) ||
                (!REVIEW_NOT_STATUS.includes(article.status) && (
                  <Button size="small" variant="contained" onClick={() => setReviewsArticle(article)}>
                    {Labels.REVIEWS}
                  </Button>
                ))}

              <Button size="small" variant="contained" onClick={() => handleOpenDialog(article)}>
                Show Progress
              </Button>
            </CardActions>
            <ReviewDialog
              articleId={reviewsArticle?.id}
              visible={article.id === reviewsArticle?.id}
              close={() => setReviewsArticle(undefined)}
            />
          </Card>
        ))}
        {selectedArtcle && (
          <OriginalFiles files={selectedArtcle.files} visible={true} close={() => setSelectedArticle(undefined)} />
        )}
        <Dialog
          maxWidth={"xs"}
          fullWidth={true}
          open={openDialog}
          onClose={handleCloseDialog}
          scroll={"body"}
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle id="form-dialog-title">Current manuscript status</DialogTitle>
          {articleDialog && (
            <DialogContent style={{ overflow: "hidden" }}>
              {dialogItemArray.map((item: DialogType) => {
                return (
                  <Grid container spacing={3} justifyContent="flex-start" alignItems="center">
                    <Grid item>
                      {checkProgress(articleDialog.status, item.status) ? (
                        <CheckCircle fontSize="large" color="primary" />
                      ) : (
                        <Cancel fontSize="large" color="error" />
                      )}
                    </Grid>
                    <Grid item>
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item>{item.icon}</Grid>
                        <Grid item>
                          <Typography>{item.iconText}</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })}
            </DialogContent>
          )}
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              {Labels.CLOSE}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
}
